import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import report from "../../../assets/report.png";
import { axiosPrivate } from '../../../api/axios';
import ButtonOne from '../../shared/ButtonOne';

ChartJS.register(...registerables);

const StepSixContent = ({ retake, headerProgress, id }: any) => {
  const [results, setResults] = useState<any>({});
  const [loading, setLoading] = useState<any>(false);
  const [chartData, setChartData] = useState<any>([]);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await axiosPrivate.get(`/assessment-result/${id}/details`);
        setResults(res.data.result);
      } catch (err) {
        toast.error("Failed to load assessment result details.");
      }
    })();
  }, [id]);

  const getRandomColor = () => {
    const r = Math.floor(Math.random() * 256);
    const g = Math.floor(Math.random() * 256);
    const b = Math.floor(Math.random() * 256);
    return `rgba(${r}, ${g}, ${b}, 0.7)`; // Using RGBA for semi-transparency
  };

  React.useEffect(() => {
    if (results?._id) {
      const pieChartData = results.competencyScores?.map((item: any) => ({
        label: item?.competencyId?.name || 'Unnamed Competency',
        value: item.score,
        backgroundColor: getRandomColor(),
      }));

      setChartData(pieChartData);
    }
  }, [results]);

  const downloadResult = async () => {
    window.location.href = `/assessments/details-report?resultId=${id}`;
  };
  const downloadAnswer = async () => {
    try {
      setLoading(true);
      const res = await axiosPrivate.get(`/assessment-result/${id}/generate-answer-key-pdf`, {
        responseType: 'blob',
      });

      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `answer-key-${id}.pdf`);
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download answer key');
    } finally {
      setLoading(false);
    }
  };

  const downloadCertificate = async () => {
    try {
      setLoading(true);
      const res = await axiosPrivate.get(`/assessment-result/${id}/certificate`, {
        responseType: 'blob', // Important for handling binary data
      });

      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificate-${id}.pdf`);
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('There was an error downloading your certificate');
    } finally {
      setLoading(false);
    }
  };

  const options = {
    plugins: {
      legend: {
        display: false,
        labels: {
          font: {
            family: 'Inter',
            size: 14,
          },
          color: '#333',
        },
      }
    },
    scales: {
      y: {
        max: 100
      }
    }
  };

  const url = `${window.location.protocol}//${window.location.host}/certificate?resultId=${id}`;

  const [copied, setCopied] = useState(false);

  React.useEffect(() => {
    if (copied) {
      // Set a timer to reset the 'copied' state to false after 5 seconds
      const timer = setTimeout(() => setCopied(false), 5000);

      // Clear the timeout if the component is unmounted or if copied changes
      return () => clearTimeout(timer);
    }
  }, [copied]);
  return (
    <>
      {loading && (
        <div className="fixed top-0 left-0 right-0 bottom-0 w-full h-screen z-50 overflow-hidden bg-gray-400 opacity-75 flex flex-col items-center justify-center">
          <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12 mb-4"></div>
          <h2 className="text-center text-white text-xl font-semibold">
            Generating...
          </h2>
          <p className="w-1/3 text-center text-white">
            Please wait a while, we are generating your result.
          </p>
        </div>
      )}
      <section className="dasboard-content lg:w-[calc(100%-15rem)] bg-white-sm flex flex-col">
        <div className="mx-[3rem] mt-[1rem]">
          <div className="flex flex-wrap items-center  md:mb-8 sm:mb-6 mb-4">
            <div className="w-full flex justify-start items-start mt-4 pb-6 mb-5">
              <div className="flex flex-col gap-2 w-full">
                <span className="font-bold text-[24px] text-[#141414]" style={{ fontFamily: '"Inter", sans-serif' }}>
                  Hello, User! 👋
                </span>
                <span className="font-medium text-[12px] text-[#989ca0]" style={{ fontFamily: '"Inter", sans-serif' }}>
                  Welcome back, track your team progress here!
                </span>
              </div>
              <div className="flex items-center gap-6">
                <div className="w-[18rem]">
                  <ButtonOne
                    text="Download Detailed Report"
                    onClick={downloadResult}
                    iconLocation={report}
                    className="p-2"
                  />
                </div>
                <div className="w-[15rem]">
                  <ButtonOne
                    text="Download Certificate"
                    onClick={downloadCertificate}
                    iconLocation={report}
                    className="p-2"
                  />
                </div>
                <div className="w-[14rem]">
                  <ButtonOne
                    text="Download Answer"
                    onClick={downloadAnswer}
                    iconLocation={report}
                    className="p-2"
                  />
                </div>


              </div>
            </div>
          </div>

          <h3 className="md:text-3xl sm:text-2xl text-xl md:mb-6 sm:mb-4 mb-2 font-medium flex items-center justify-start">
            <div className="flex items-center space-x-4">
              <span style={{ fontFamily: '"Inter", sans-serif', fontSize: '35px', fontStyle: 'italic' }}>YOUR OVERALL SCORE IS</span>
              <div
                className={
                  (results?.totalScore < 50
                    ? 'bg-red-500'
                    : results?.totalScore < 70
                      ? 'bg-yellow-500'
                      : 'bg-green-500') +
                  ' px-6 py-2 rounded-xl text-white text-base font-bold'
                }
                style={{ fontFamily: '"Inter", sans-serif', fontSize: '25px', fontStyle: 'italic' }}
              >
                {results?.totalScore?.toFixed(2)}%
              </div>
              <div className="w-[4rem] relative" >
                {/* Show Copied Text separately (e.g., above the icon) */}
                {copied && (
                  <span className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-green-500 text-sm font-medium" style={{ fontFamily: '"Inter", sans-serif' }}>
                    Copied!
                  </span>
                )}

                <div className="cursor-pointer inline-flex items-center border-b w-full border-white-300 leading-9 lg:text-base text-sm text-gray-black bg-[#263238] text-white justify-center rounded-lg sm:py-4 py-2 sm:text-base mb-4">
                  <CopyToClipboard text={url} onCopy={() => setCopied(true)}>
                    <span className="text-blue-400 hover:text-blue-600">
                      <FontAwesomeIcon icon={faCopy} className="mr-1" />
                    </span>
                  </CopyToClipboard>
                </div>
              </div>
            </div>
          </h3>
          <div className="lg:w-full w-full px-3">
            <div className="md:p-8 py-6 px-4 md:rounded-2xl rounded-xl md:mb-6 mb-4">
              <Bar width={"100%"} height={"50%"} data={{
                labels: chartData.map((data: any) => data.label),
                datasets: [
                  {
                    data: chartData.map((data: any) => {
                      return data.value
                    }),
                    backgroundColor: '#eab308',
                  },
                ],
              }} options={options} />

            </div>
          </div>
          <div className="flex flex-wrap justify-between -mx-3 font-['Inter']">
            <div className="w-full px-3">
              <div className="md:p-8 py-6 px-4 md:rounded-2xl rounded-xl  md:mb-6 mb-4">
                <div className="grid md:grid-cols-2 grid-cols-1 gap-6">
                  {results?.competencyScores?.map((compItem: any, idx: number) => {
                    const compId = compItem?.competencyId?._id || compItem?.competencyId;

                    // Find competency object from assessment.jobRole.competencies
                    const fullCompetency = results?.assessment?.Jobrole?.competencies?.find(
                      (comp: any) => comp?._id?.toString() === compId?.toString()
                    );

                    const capabilityIds = fullCompetency?.capabilities?.map((c: any) =>
                      c?._id?.toString()
                    ) || [];

                    // Get capability scores that match the current competency's capabilities
                    const filteredCapabilities = results?.capabilityScores?.filter(
                      (cap: any) => {
                        const capId = cap?.capabilityId?._id || cap?.capabilityId;
                        return capabilityIds.includes(capId?.toString());
                      }
                    );

                    return (
                      <div key={idx}>
                        {/* Competency Section */}
                        <div className="sm:mb-6 mb-4">
                          <div className="flex items-center justify-between sm:mb-4 mb-2">
                            <div
                              className="mb-1 text-base font-medium"
                              style={{ fontFamily: '"Inter", sans-serif', fontSize: '20px' }}
                            >
                              {compItem?.competencyId?.name || 'Unnamed Competency'}
                            </div>
                            <div
                              className="mb-1 text-base font-medium"
                              style={{ fontFamily: '"Inter", sans-serif' }}
                            >
                              {compItem?.score?.toFixed(2)}%
                            </div>
                          </div>
                          <div className="w-full bg-gray-300 rounded-full h-2 mb-4 dark:bg-gray-700">
                            <div
                              className="bg-amber-300 h-2 rounded-full"
                              style={{ width: `${compItem?.score}%` }}
                            />
                          </div>
                        </div>

                        {/* Capability Section */}
                        {filteredCapabilities?.map((capability: any, capIdx: number) => (
                          <div
                            className="sm:mb-6 mb-4 ml-4 bg-gray-100 p-3 rounded-lg"
                            key={capIdx}
                          >
                            <div className="flex items-center justify-between sm:mb-4 mb-2">
                              <div
                                className="mb-1 text-base font-medium"
                                style={{ fontFamily: '"Inter", sans-serif' }}
                              >
                                {capability?.capabilityId?.name || capability?.capabilityId?.title || 'Unnamed Capability'}
                              </div>
                              <div
                                className="mb-1 text-base font-medium"
                                style={{ fontFamily: '"Inter", sans-serif' }}
                              >
                                {capability?.score?.toFixed(2)}%
                              </div>
                            </div>
                            <div className="w-full bg-gray-300 rounded-full h-2 mb-4 dark:bg-gray-700">
                              <div
                                className="bg-amber-300 h-2 rounded-full"
                                style={{ width: `${capability?.score || 0}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}

                </div>
              </div>
            </div>
          </div>

        </div>

      </section>
    </>
  );
};

export default StepSixContent;
